/* eslint-disable no-param-reassign */
import React from 'react';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { InfoBlock } from '@picsio/ui';

import l10n from '../../shared/strings';
import {
  getLastLiveChannelsForVariantGroup,
  markProductChannelsAsDraft,
} from './helpers/channelHelpers';
import { resolveChannelLabels } from './helpers/showPublishedToChannelToast';
import { syncProductHasVariants } from './helpers/productVariantsHelpers';

const getOptionValueVariants = (product, optionName, value) => (product?.variants || []).filter(
  (variant) => (variant.options || {})[optionName] === value,
);

export const deleteOptionValue = createAsyncThunk(
  'products/deleteOptionValue',
  async ({
    productId, optionName, value, listingVariantIds = [],
  }, {
    extra: { Logger, Toast, sdk },
    getState,
  }) => {
    try {
      const product = getState().products.items.find(({ _id }) => _id === productId);

      const option = product?.availableOptions?.find((availableOption) => availableOption?.name === optionName);

      if (!option?.name) {
        throw new Error('Option not found');
      }

      const channelsToUnpublish = getLastLiveChannelsForVariantGroup(
        product,
        getOptionValueVariants(product, optionName, value),
        listingVariantIds,
        getState().products,
      );

      if (listingVariantIds.length) {
        /** Sequential intentionally — server checks isLastLiveListingVariant per variant; parallel would race: all variants see each other as live and none triggers the listing wipe. */
        // eslint-disable-next-line no-restricted-syntax
        for (const id of listingVariantIds) {
          // eslint-disable-next-line no-await-in-loop
          await sdk.products.deleteVariant(id);
        }
      }

      const values = (option.values || [])
        .filter((optionValue) => (optionValue?.name ?? optionValue) !== value)
        .map((optionValue) => optionValue?.name ?? optionValue);

      const { data } = await sdk.products.updateOption(productId, {
        name: option.name,
        values,
      });
      return { data, channelsToUnpublish };
    } catch (error) {
      Toast(
        'Option value is not deleted. Refresh and try again',
        { autoClose: false, type: 'error' },
      );
      Logger.error(new Error('Error option value deleting'), { error }, [
        'DeleteOptionValueFailed',
        { errorMessage: 'Option value is not deleted on the backend.' },
      ]);
      throw error;
    }
  },
  {
    condition: async ({
      productId, optionName, value, listingVariantIds = [],
    }, { getState, extra: { showDialogAsync } }) => {
      const product = getState().products.items.find(({ _id }) => _id === productId);
      const channelsToUnpublish = getLastLiveChannelsForVariantGroup(
        product,
        getOptionValueVariants(product, optionName, value),
        listingVariantIds,
        getState().products,
      );

      if (!channelsToUnpublish.length) return true;

      const channelNames = resolveChannelLabels(channelsToUnpublish, getState().user?.team?.salesChannels || []);

      try {
        await showDialogAsync({
          title: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.TITLE_CONFIRM_VALUE(value),
          text: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.TEXT_LAST_LIVE_VALUE(value, channelNames),
          children: <InfoBlock className="m-t-16">{l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.LAST_LIVE_VARIANTS_NOTE}</InfoBlock>,
          textBtnCancel: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.BTN_CANCEL,
          textBtnOk: l10n.DIALOGS.PRODUCT_DIALOGS.DELETE_MASTER_OPTION.BTN_OK,
          buttonOkColor: 'danger',
        }).promise;
        return true;
      } catch {
        return false;
      }
    },
  },
);

export const reducer = (builder) => {
  builder.addCase(
    deleteOptionValue.pending,
    (state, { meta }) => {
      state.items.forEach((product) => {
        if (product._id === meta.arg.productId) {
          product.isBusy = true;
        }
      });
    },
  ).addCase(
    deleteOptionValue.fulfilled,
    (state, { payload, meta }) => {
      state.items = state.items.map((product) => {
        if (product._id === meta.arg.productId) {
          const updatedProduct = {
            ...product,
            isBusy: false,
            variants: payload.data.variants,
            availableOptions: (product.availableOptions || []).map((opt) => (
              opt.name === meta.arg.optionName ? { ...opt, values: payload.data.option.values } : opt
            )),
          };
          syncProductHasVariants(updatedProduct);
          markProductChannelsAsDraft(updatedProduct, payload.channelsToUnpublish, state);
          return updatedProduct;
        }
        return product;
      });
    },
  ).addCase(
    deleteOptionValue.rejected,
    (state, { meta, error }) => {
      state.items.forEach((product) => {
        if (product._id === meta.arg.productId) {
          product.isBusy = false;
        }
      });
      state.error = error;
    },
  );
};
